import { useState, useEffect, useRef, useCallback } from 'react';
import type { UserProfileResponse } from '@nova-mine/shared';
import { api } from '../services/api.js';

export function useMining(onHaptic?: () => void) {
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimSuccessMessage, setClaimSuccessMessage] = useState<string | null>(null);

  // Live ticking state
  const [liveAccrued, setLiveAccrued] = useState(0);
  const [liveMainAccrued, setLiveMainAccrued] = useState(0);
  const [liveAutoAccrued, setLiveAutoAccrued] = useState(0);
  const [liveStarsAccrued, setLiveStarsAccrued] = useState(0);

  const baselineRef = useRef<{
    serverTime: number;
    localFetchTime: number;
    mainAccrued: number;
    mainRate: number;
    autoAccrued: number;
    autoRate: number;
    starsAccrued: number;
    starsRate: number;
    totalRate: number;
  }>({
    serverTime: Date.now(),
    localFetchTime: Date.now(),
    mainAccrued: 0,
    mainRate: 0,
    autoAccrued: 0,
    autoRate: 0,
    starsAccrued: 0,
    starsRate: 0,
    totalRate: 0,
  });

  const refresh = useCallback(async () => {
    try {
      const data = await api.getProfile();
      setProfile(data);

      const now = Date.now();
      baselineRef.current = {
        serverTime: data.mining.aggregate.server_time,
        localFetchTime: now,
        mainAccrued: data.mining.main.accrued_coins,
        mainRate: data.mining.main.rate_per_second,
        autoAccrued: data.mining.auto.accrued_coins,
        autoRate: data.mining.auto.rate_per_second,
        starsAccrued: data.mining.stars.accrued_coins,
        starsRate: data.mining.stars.rate_per_second,
        totalRate: data.mining.aggregate.total_rate_per_second,
      };

      setLiveMainAccrued(data.mining.main.accrued_coins);
      setLiveAutoAccrued(data.mining.auto.accrued_coins);
      setLiveStarsAccrued(data.mining.stars.accrued_coins);
      setLiveAccrued(data.mining.aggregate.total_accrued);
    } catch (err) {
      console.error('[useMining] Failed to fetch profile', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Real-time animation ticker loop (~20 FPS)
  useEffect(() => {
    const interval = setInterval(() => {
      const b = baselineRef.current;
      const elapsedSeconds = Math.max(0, (Date.now() - b.localFetchTime) / 1000);

      const curMain = b.mainAccrued + elapsedSeconds * b.mainRate;
      const curAuto = b.autoAccrued + elapsedSeconds * b.autoRate;
      const curStars = b.starsAccrued + elapsedSeconds * b.starsRate;
      const curTotal = curMain + curAuto + curStars;

      setLiveMainAccrued(curMain);
      setLiveAutoAccrued(curAuto);
      setLiveStarsAccrued(curStars);
      setLiveAccrued(curTotal);
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const claim = async (
    minerType: 'all' | 'main' | 'auto' | 'stars' = 'all',
    minerId?: string
  ) => {
    if (isClaiming) return;
    try {
      setIsClaiming(true);
      if (onHaptic) onHaptic();

      const result = await api.claimCoins(minerType, minerId);

      if (result.claimed_amount > 0) {
        setClaimSuccessMessage(`+${result.claimed_amount.toFixed(4)} Nova Coins Claimed!`);
        setTimeout(() => setClaimSuccessMessage(null), 3500);
      }

      await refresh();
      return result;
    } catch (err: unknown) {
      console.error('[Claim Error]', err);
      throw err;
    } finally {
      setIsClaiming(false);
    }
  };

  return {
    profile,
    isLoading,
    isClaiming,
    claimSuccessMessage,
    liveAccrued,
    liveMainAccrued,
    liveAutoAccrued,
    liveStarsAccrued,
    claim,
    refresh,
  };
}
